import { useState, useCallback, useEffect, useMemo } from 'react';

import { Colors, Button, Flex, Input, Text } from '@revenuemarket/cds';
import { Toast } from '@revenuemarket/ui';
import { validateEmail } from '@revenuemarket/utils';
import { mixpanelTrack } from '@revenuemarket/utils/mixpanel';
import { Checkbox, validatePhoneNumber } from '@revenuemarket/vds';
import { Space, Select } from 'antd';
import { match } from 'ts-pattern';
import type { PartnerInvitationContactType } from '~apis/company/partnerInvitation';
import CommonModal from '~components/ui/Modal/CommonModal';
import { HOME_RED_DOT_DETAIL_NUDGE_EXPERIMENT_KEY } from '~experiments/constants';
import { useExperiment } from '~experiments/hooks/useExperiment';
import { useUpdateClientState } from '~hooks/queries/clientState/query';
import { useCreatePartnerInvitation } from '~hooks/queries/partnerInvitation/query';
import { useCompanyId } from '~hooks/useCompanyId';
import { css } from '~libs/emotion';
import { MIXPANEL_EVENT } from '~libs/mixpanel/event';

const BLOG_URL =
  'https://clobe.ai/blog/settlement-automation-clobe-connect?utm_source=inapp_button&utm_medium=inapp&utm_campaign=260121_partners-settelment-automation&utm_term=inhouse-client&utm_content=클로브커넥트-자세히보기';
const EVENT_FORM_URL = 'https://forms.gle/ojh4n2kPCjbgm5i57';

export const CLIENT_PARTNER_INVITE_POPUP_HIDE_KEY = 'clientPartnerInvitePopupHide';
const PARTNER_INVITATION_SENT_KEY = 'partnerInvitationSent';

interface SignupPartnersIntroModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const SignupPartnersIntroModal = ({ isOpen, onClose }: SignupPartnersIntroModalProps) => {
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [contactType, setContactType] = useState<PartnerInvitationContactType>('EMAIL');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [isTouched, setIsTouched] = useState(false);
  const [isInvited, setIsInvited] = useState(false);
  const [doNotShowAgain, setDoNotShowAgain] = useState(false);

  // [EXPERIMENT: aa_test] A/B 테스트 이벤트 로깅용
  const { abTestLogValue } = useExperiment(HOME_RED_DOT_DETAIL_NUDGE_EXPERIMENT_KEY);

  const { mutate: updateClientState } = useUpdateClientState();
  const { mutateAsync: createPartnerInvitation, isLoading: isCreatingInvitation } =
    useCreatePartnerInvitation();
  const { companyId } = useCompanyId();

  // 로컬 스토리지에서 초대 완료 여부 읽어오기
  useEffect(() => {
    if (companyId) {
      const storageKey = `${PARTNER_INVITATION_SENT_KEY}_${companyId}`;
      const savedValue = localStorage.getItem(storageKey);
      setIsInvited(savedValue === 'true');
    } else {
      setIsInvited(false);
    }
  }, [companyId]);

  const handleCheckboxChange = useCallback((e: { target: { checked: boolean } }) => {
    setDoNotShowAgain(e.target.checked);
  }, []);

  const handleClose = useCallback(() => {
    if (doNotShowAgain) {
      updateClientState({ name: CLIENT_PARTNER_INVITE_POPUP_HIDE_KEY, value: 'true' });
    }
    onClose();
  }, [doNotShowAgain, onClose, updateClientState]);

  const validateEmailInput = useCallback((value: string) => {
    if (!value) {
      return '이메일을 입력해주세요.';
    }
    if (!validateEmail(value)) {
      return '이메일 형식에 맞춰 입력해주세요.';
    }
    return null;
  }, []);

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);

    if (isTouched) {
      setEmailError(validateEmailInput(value));
    }
  };

  const handleEmailBlur = () => {
    setIsTouched(true);
    setEmailError(validateEmailInput(email));
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPhone(value);

    if (isTouched) {
      const validated = validatePhoneNumber(value);
      if (!validated.isValid) {
        setPhoneError(validated.error);
      } else {
        setPhoneError(null);
      }
    }
  };

  const handlePhoneBlur = () => {
    setIsTouched(true);
    const validated = validatePhoneNumber(phone);
    if (!validated.isValid) {
      setPhoneError(validated.error);
    } else {
      setPhoneError(null);
    }
  };

  const handleInvitePartner = async () => {
    if (!companyId) return;

    mixpanelTrack(MIXPANEL_EVENT.PARTNERS.CLICK_INVITE_PARTNER_BUTTON, {
      ab_test: abTestLogValue,
    });

    try {
      await createPartnerInvitation({
        companyId,
        request: {
          invitationPurpose: 'PARTNER_INVITE',
          contactType,
          contactValue: match(contactType)
            .with('EMAIL', () => email)
            .with('SMS', () => phone)
            .exhaustive(),
        },
      });
      setIsInvited(true);
      localStorage.setItem(`${PARTNER_INVITATION_SENT_KEY}_${companyId}`, 'true');
      const contactTypeMessage: Record<PartnerInvitationContactType, string> = {
        EMAIL: '이메일을',
        SMS: '메시지를',
      };
      Toast.success(`세무대리인에게 초대 ${contactTypeMessage[contactType]} 발송했습니다.`);
    } catch {
      const contactTypeMessage: Record<PartnerInvitationContactType, string> = {
        EMAIL: '이메일',
        SMS: '메시지',
      };
      Toast.error(
        `초대 ${contactTypeMessage[contactType]} 발송에 실패했습니다. 다시 시도해주세요.`,
      );
    }
  };

  const isValid = useMemo(() => {
    switch (contactType) {
      case 'EMAIL':
        return email.length > 0 && emailError == null;
      case 'SMS':
        return phone.length > 0 && phoneError == null;
      default:
        return false;
    }
  }, [contactType, email, emailError, phone, phoneError]);

  return (
    <CommonModal
      title={
        <Text textStyle="24_600" style={{ color: Colors.gray800 }}>
          세무대리인을 초대하여 함께 사용해보세요
        </Text>
      }
      open={isOpen}
      onCancel={handleClose}
      centered
      footer={null}
      width={540}
      className={css`
        &&& .ant-modal-content {
          max-height: 90vh;
          overflow-y: auto;
        }
      `}
    >
      <Flex direction="column" gap={12}>
        <Flex direction="column" gap={12}>
          <Text textStyle="14_400" style={{ color: Colors.gray700 }}>
            세무대리인과 함께 더욱 효율적으로 결산 업무를 진행해보세요. 세무대리인이 클로브커넥트에
            가입하면 다음 기능들을 무료로 사용할 수 있습니다.
          </Text>
          <Flex
            direction="column"
            gap={8}
            className={css`
              padding: 16px;
              background-color: ${Colors.gray50};
              border-radius: 8px;
            `}
          >
            <Flex gap={8}>
              <Text textStyle="14_400" style={{ color: Colors.gray700 }}>
                •
              </Text>
              <Text textStyle="14_400" style={{ color: Colors.gray700 }}>
                <span style={{ fontWeight: 600 }}>업무 시간 단축:</span> 전표 수집부터 처리까지
                자동화
              </Text>
            </Flex>
            <Flex gap={8}>
              <Text textStyle="14_400" style={{ color: Colors.gray700 }}>
                •
              </Text>
              <Text textStyle="14_400" style={{ color: Colors.gray700 }}>
                <span style={{ fontWeight: 600 }}>검토 업무 간편화:</span> 자동 처리된 전표만 모아서
                한 눈에 검토
              </Text>
            </Flex>
            <Flex gap={8}>
              <Text textStyle="14_400" style={{ color: Colors.gray700 }}>
                •
              </Text>
              <Text textStyle="14_400" style={{ color: Colors.gray700 }}>
                <span style={{ fontWeight: 600 }}>위하고 연동:</span> 완성된 전표는 위하고에
                업로드하여 결산 마감
              </Text>
            </Flex>
          </Flex>
          <Text textStyle="14_600" style={{ color: Colors.gray700 }}>
            <a
              href={BLOG_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={css`
                color: ${Colors.blue500};
                text-decoration: underline;
                cursor: pointer;
                font-weight: 600;
              `}
            >
              클로브커넥트 자세히 보기 →
            </a>
          </Text>
        </Flex>

        <Flex direction="column" gap={8} style={{ paddingTop: 12 }}>
          <Flex direction="column" gap={4}>
            <Text textStyle="14_600" style={{ color: Colors.gray700 }}>
              세무대리인 정보
            </Text>
            <Space.Compact size="large">
              <Select
                size="large"
                value={contactType}
                options={[
                  { label: '이메일', value: 'EMAIL' },
                  { label: 'SMS', value: 'SMS' },
                ]}
                onChange={(value) => {
                  setContactType(value);
                  setIsTouched(false);
                  setEmailError(null);
                  setPhoneError(null);
                }}
              />
              {contactType === 'EMAIL' && (
                <Input
                  inputSize="large"
                  type="email"
                  placeholder="세무대리인 이메일을 입력해주세요"
                  value={email}
                  onChange={handleEmailChange}
                  onBlur={handleEmailBlur}
                  status={emailError ? 'error' : undefined}
                  wrapperStyle={{ width: '100%' }}
                />
              )}
              {contactType === 'SMS' && (
                <Input
                  inputSize="large"
                  type="phone"
                  placeholder="세무대리인 전화번호를 입력해주세요"
                  value={phone}
                  onChange={handlePhoneChange}
                  onBlur={handlePhoneBlur}
                  status={phoneError != null ? 'error' : undefined}
                  wrapperStyle={{ width: '100%' }}
                />
              )}
            </Space.Compact>
            {emailError && (
              <Text
                textStyle="14_400"
                style={{
                  color: Colors.red500,
                }}
              >
                {emailError}
              </Text>
            )}
            {phoneError != null && (
              <Text
                textStyle="14_400"
                style={{
                  color: Colors.red500,
                }}
              >
                {phoneError}
              </Text>
            )}
          </Flex>

          <Button
            buttonType="primary"
            buttonSize="xlarge"
            onClick={handleInvitePartner}
            disabled={!isValid || isCreatingInvitation}
            className={css`
              width: 100%;
            `}
          >
            무료로 세무대리인 초대하기
          </Button>
          <Flex
            direction="column"
            gap={8}
            className={css`
              width: 100%;
              margin-top: 24px;
              padding: 16px;
              background-color: ${Colors.gray50};
              border-radius: 8px;
            `}
          >
            <Text textStyle="16_600" style={{ color: Colors.gray700 }}>
              🎉 현금 리워드: 세무대리인 소개 이벤트
            </Text>
            <Flex direction="column" gap={8}>
              <Text textStyle="14_400" style={{ color: Colors.gray700 }}>
                소개해주신 세무대리인이 가입 후 공동인증서 연동 시
              </Text>
              <Text textStyle="14_400" style={{ color: Colors.gray700 }}>
                법인고객당 1만원 현금 리워드 지급 (평균 100만원 지급 예상)
              </Text>
            </Flex>
            <Button
              buttonType="weak"
              buttonSize="xlarge"
              onClick={() => {
                mixpanelTrack(MIXPANEL_EVENT.PARTNERS.CLICK_PARTNER_EVENT_FORM, {
                  ab_test: abTestLogValue,
                });
                window.open(EVENT_FORM_URL, '_blank', 'noopener,noreferrer');
              }}
              disabled={!isInvited}
            >
              세무대리인 추천 이벤트 정보 입력
            </Button>
          </Flex>
        </Flex>
        <Flex justify="space-between" align="center">
          <Flex align="center" gap={8}>
            <Checkbox checked={doNotShowAgain} onChange={handleCheckboxChange} />
            <Text textStyle="13_400" style={{ color: Colors.gray600 }}>
              다시 보지 않기
            </Text>
          </Flex>

          <Button
            buttonType="transparent"
            buttonSize="xlarge"
            onClick={() => {
              mixpanelTrack(MIXPANEL_EVENT.PARTNERS.CLICK_DISMISS_INTRO_MODAL, {
                ab_test: abTestLogValue,
                doNotShowAgain,
              });
              handleClose();
            }}
          >
            다음에 하기
          </Button>
        </Flex>
      </Flex>
    </CommonModal>
  );
};

export default SignupPartnersIntroModal;
